import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyP2,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertCircle, Loader2, Share2, UserRoundPlus, UsersRound } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

export type MultiplayerHandoffKind =
  | 'share_session'
  | 'unshare_session'
  | 'list_session_shares'
  | 'invite_team_member'
  | 'remove_team_member'
  | 'share_template'
  | 'unshare_template';

export interface MultiplayerHandoffBlockProps {
  kind: MultiplayerHandoffKind;
  status: 'pending' | 'completed' | 'error';
  /** Primary one-line summary (e.g. "Shared with Alex Rivera · edit"). */
  summary: string;
  /** Optional secondary detail lines (member names, emails). */
  details?: string[];
  error?: string;
  sessionId?: string;
  onOpenSession?: (sessionId: string) => void;
  className?: string;
}

const KIND_ICON: Record<MultiplayerHandoffKind, typeof Share2> = {
  share_session: Share2,
  unshare_session: Share2,
  list_session_shares: UsersRound,
  invite_team_member: UserRoundPlus,
  remove_team_member: UserRoundPlus,
  share_template: Share2,
  unshare_template: Share2,
};

/**
 * Compact handoff card for multiplayer tool results — colleague-facing copy
 * plus an optional Open session affordance (canvas).
 */
export const MultiplayerHandoffBlock = ({
  kind,
  status,
  summary,
  details,
  error,
  sessionId,
  onOpenSession,
  className,
}: MultiplayerHandoffBlockProps): JSX.Element => {
  const Icon = KIND_ICON[kind];
  const canOpen = Boolean(sessionId && onOpenSession && status === 'completed');

  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full items-start gap-3 rounded-xl border border-validation-destructive/30 bg-surface-accent p-4',
          className
        )}
        data-testid="multiplayer-handoff-block"
        data-status="error"
        data-kind={kind}
      >
        <AlertCircle
          className="mt-0.5 size-5 shrink-0 text-validation-destructive"
          aria-hidden
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyPBold className="text-text-primary">{summary}</TypographyPBold>
          <TypographyCaption className="text-validation-destructive">
            {error ?? (
              <FormattedMessage defaultMessage="Something went wrong" id="JqiqNj0Ooi" />
            )}
          </TypographyCaption>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex w-full items-start gap-3 rounded-xl border border-border bg-surface-accent p-4',
        className
      )}
      data-testid="multiplayer-handoff-block"
      data-status={status}
      data-kind={kind}
    >
      {status === 'pending' ? (
        <Loader2
          className="mt-0.5 size-5 shrink-0 animate-spin text-text-tertiary"
          aria-hidden
        />
      ) : (
        <Icon className="mt-0.5 size-5 shrink-0 text-text-tertiary" aria-hidden />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <TypographyPBold className="text-text-primary">{summary}</TypographyPBold>
        {details?.map((line) => (
          <TypographyP2 key={line} className="text-text-secondary">
            {line}
          </TypographyP2>
        ))}
        {status === 'pending' && (
          <TypographyCaption className="text-text-tertiary">
            <FormattedMessage defaultMessage="Working…" id="hsvAwBKpgC" />
          </TypographyCaption>
        )}
      </div>
      {canOpen && sessionId && onOpenSession ? (
        <Button
          variant="secondary"
          size="sm"
          className="shrink-0"
          onClick={() => onOpenSession(sessionId)}
        >
          <FormattedMessage defaultMessage="Open session" id="lTKuAGoTLG" />
        </Button>
      ) : null}
    </div>
  );
};
